import { Editor } from '@tiptap/core';
import {
  Bold01,
  Dotpoints01,
  Italic01,
  Link03,
  LinkBroken02,
  NumberedList,
  Underline01,
} from 'ethena-icons';
import produce from 'immer';
import _ from 'lodash';
import { ElementType } from 'react';
import { FaAlignCenter, FaAlignLeft, FaAlignRight } from 'react-icons/fa';
import {
  RichTextEditorButton,
  RichTextEditorSelect,
  RichTextEditorTextStyle,
} from './types';

type RichTextEditorSelectOption = RichTextEditorTextStyle;

export type RichTextEditorMenuItemConfiguration =
  | RichTextEditorButton
  | RichTextEditorSelect
  | IRichTextEditorSelectConfiguration;

export interface IRichTextEditorSelectConfiguration {
  id: RichTextEditorSelect;
  orderedOptions: RichTextEditorSelectOption[];
}

export interface IMenuButtonConfiguration {
  id: RichTextEditorButton;
  getIsDisabled?: (editor: Editor) => boolean;
  getIsSelected?: (editor: Editor) => boolean;
  icon: ElementType;
  label: string;
  onClick: (editor: Editor, callback?: (editor: Editor) => void) => void;
}

export interface IMenuSelectConfiguration {
  id: RichTextEditorSelect;
  label: string;
  options: IMenuSelectOption[];
}

export interface IMenuSelectOption {
  classNameKey?: string;
  getIsSelected: (editor: Editor) => boolean;
  id: RichTextEditorSelectOption;
  label: string;
  onSelect: (editor: Editor) => void;
}

const BUTTON_OPTIONS: IMenuButtonConfiguration[] = [
  {
    id: RichTextEditorButton.BOLD,
    label: 'Bold',
    icon: Bold01,
    getIsSelected: (editor) => editor.isActive('bold'),
    onClick: (editor) => editor.chain().focus().toggleBold().run(),
  },
  {
    id: RichTextEditorButton.ITALIC,
    label: 'Italic',
    icon: Italic01,
    getIsSelected: (editor) => editor.isActive('italic'),
    onClick: (editor) => editor.chain().focus().toggleItalic().run(),
  },
  {
    id: RichTextEditorButton.UNDERLINE,
    label: 'Underline',
    icon: Underline01,
    getIsSelected: (editor) => editor.isActive('underline'),
    onClick: (editor) => editor.chain().focus().toggleUnderline().run(),
  },
  {
    id: RichTextEditorButton.BULLETED_LIST,
    label: 'Bulleted List',
    icon: Dotpoints01,
    getIsSelected: (editor) => editor.isActive('bulletList'),
    onClick: (editor) => editor.chain().focus().toggleBulletList().run(),
  },
  {
    id: RichTextEditorButton.NUMBERED_LIST,
    label: 'Numbered List',
    icon: NumberedList,
    getIsSelected: (editor) => editor.isActive('orderedList'),
    onClick: (editor) => editor.chain().focus().toggleOrderedList().run(),
  },
  {
    id: RichTextEditorButton.ALIGN_LEFT,
    label: 'Align Left',
    icon: FaAlignLeft,
    getIsSelected: (editor) => editor.isActive({ textAlign: 'left' }),
    onClick: (editor) => editor.chain().focus().setTextAlign('left').run(),
  },
  {
    id: RichTextEditorButton.ALIGN_CENTER,
    label: 'Align Center',
    icon: FaAlignCenter,
    getIsSelected: (editor) => editor.isActive({ textAlign: 'center' }),
    onClick: (editor) => editor.chain().focus().setTextAlign('center').run(),
  },
  {
    id: RichTextEditorButton.ALIGN_RIGHT,
    label: 'Align Right',
    icon: FaAlignRight,
    getIsSelected: (editor) => editor.isActive({ textAlign: 'right' }),
    onClick: (editor) => editor.chain().focus().setTextAlign('right').run(),
  },
  {
    id: RichTextEditorButton.SET_LINK,
    label: 'Set Link',
    icon: Link03,
    onClick: (editor, callback) => callback(editor),
  },
  {
    id: RichTextEditorButton.UNSET_LINK,
    label: 'Unset Link',
    icon: LinkBroken02,
    getIsDisabled: (editor) => !editor?.isActive('link'),
    onClick: (editor) => editor.chain().focus().unsetLink().run(),
  },
];

const SELECT_OPTIONS: IMenuSelectConfiguration[] = [
  {
    id: RichTextEditorSelect.TEXT_STYLE,
    label: 'Text Style',
    options: _.compact([
      {
        id: RichTextEditorTextStyle.NORMAL,
        label: 'Normal text',
        classNameKey: 'normal',
        getIsSelected: (editor) => editor.isActive('paragraph'),
        onSelect: (editor) => editor.chain().focus().setParagraph().run(),
      },
      {
        id: RichTextEditorTextStyle.H2,
        label: 'Header',
        classNameKey: 'h2',
        getIsSelected: (editor) => editor.isActive('heading', { level: 2 }),
        onSelect: (editor) =>
          editor.chain().focus().toggleHeading({ level: 2 }).run(),
      },
    ]),
  },
];

const getMenuItem = (
  item: RichTextEditorMenuItemConfiguration
): IMenuButtonConfiguration | IMenuSelectConfiguration => {
  const getOption = (item: RichTextEditorButton | RichTextEditorSelect) => {
    if (item in RichTextEditorButton) {
      return _.find(BUTTON_OPTIONS, (x) => x.id === item);
    }
    if (item in RichTextEditorSelect) {
      return _.find(SELECT_OPTIONS, (x) => x.id === item);
    }
  };
  if (_.isObject(item)) {
    const config = getOption(item.id);
    return item.orderedOptions
      ? produce(config as IMenuSelectConfiguration, (draft) => {
          draft.options = _.chain(item.orderedOptions)
            .map((item) => _.find(draft.options, (x) => x.id === item))
            .compact()
            .value();
        })
      : config;
  }
  return getOption(item);
};

export const getMenuItems = (
  orderedItems: (
    | RichTextEditorButton
    | RichTextEditorSelect
    | IRichTextEditorSelectConfiguration
  )[]
): (IMenuButtonConfiguration | IMenuSelectConfiguration)[] => {
  return _.chain(orderedItems).map(getMenuItem).compact().value();
};
